import React, { useMemo, useState } from 'react';
import {
  Box,
  TextField,
  Button,
  Typography,
  Alert,
  CircularProgress,
  Container,
  Paper,
} from '@mui/material';
import { Link as RouterLink, useNavigate, useSearchParams } from 'react-router-dom';
import { apiService, ApiError } from '../../services/apiService';
import { validatePasswordStrength } from '../../utils/passwordValidation';

/**
 * Completes a password reset. Reads the one-time token from the URL query
 * (`/reset-password?token=...`), lets the user choose a new password, and calls
 * the backend confirm endpoint.
 */
const ResetPasswordForm: React.FC = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const token = searchParams.get('token') || '';

  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [done, setDone] = useState(false);

  const passwordError = useMemo(() => validatePasswordStrength(newPassword), [newPassword]);
  const mismatch = confirmPassword !== '' && newPassword !== confirmPassword;
  const canSubmit = !!token && !passwordError && !mismatch && newPassword !== '' && confirmPassword !== '';

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!canSubmit) return;
    setIsLoading(true);
    setError(null);
    try {
      const res = await apiService.confirmPasswordReset(token, newPassword);
      if (res.data?.success) {
        setDone(true);
        setTimeout(() => navigate('/login'), 2500);
      } else {
        setError(res.data?.message || 'Could not reset your password. The link may have expired.');
      }
    } catch (err) {
      const msg =
        err instanceof ApiError
          ? (err.status === 400
              ? 'This reset link is invalid or has expired. Please request a new one.'
              : err.message)
          : 'Something went wrong. Please try again.';
      setError(msg);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Container component="main" maxWidth="sm">
      <Box sx={{ marginTop: 8, display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
        <Paper elevation={3} sx={{ padding: 4, width: '100%' }}>
          <Box sx={{ textAlign: 'center', mb: 3 }}>
            <Typography component="h1" variant="h4" gutterBottom>
              Choose a new password
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Pick a strong password you don't use anywhere else.
            </Typography>
          </Box>

          {!token && (
            <Alert severity="error" sx={{ mb: 2 }}>
              This reset link is missing its token. Please use the link from your email, or{' '}
              request a new one.
            </Alert>
          )}

          {error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}

          {done ? (
            <Box>
              <Alert severity="success" sx={{ mb: 2 }}>
                Your password has been reset. Redirecting you to sign in…
              </Alert>
              <Button fullWidth variant="contained" component={RouterLink} to="/login">
                Go to sign in
              </Button>
            </Box>
          ) : (
            <Box component="form" onSubmit={handleSubmit} sx={{ mt: 1 }}>
              <TextField
                margin="normal"
                required
                fullWidth
                name="newPassword"
                label="New password"
                type="password"
                id="newPassword"
                autoComplete="new-password"
                autoFocus
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                disabled={isLoading || !token}
                error={newPassword !== '' && !!passwordError}
                helperText={newPassword !== '' && passwordError ? passwordError : 'At least 8 characters, with an uppercase letter, a lowercase letter, and a number.'}
              />
              <TextField
                margin="normal"
                required
                fullWidth
                name="confirmPassword"
                label="Confirm new password"
                type="password"
                id="confirmPassword"
                autoComplete="new-password"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                disabled={isLoading || !token}
                error={mismatch}
                helperText={mismatch ? 'Passwords do not match' : ''}
              />
              <Button
                type="submit"
                fullWidth
                variant="contained"
                sx={{ mt: 3, mb: 2 }}
                disabled={isLoading || !canSubmit}
              >
                {isLoading ? <CircularProgress size={24} /> : 'Reset password'}
              </Button>
              <Button fullWidth variant="text" component={RouterLink} to="/login" disabled={isLoading}>
                Back to sign in
              </Button>
            </Box>
          )}
        </Paper>
      </Box>
    </Container>
  );
};

export default ResetPasswordForm;
